import type { TemplateDependencyValidationError } from '@festina/configuration-general-types/configuration'
import type { Optional } from '@festina-life/core'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { useCallback } from 'react'

import { useLayoutContext } from '../../context/layoutContext'
import { useComparableField } from '../../context/useComparableField'
import {
  ConfigurationFieldType,
  isSubTemplatedField,
  type NonConfigurationSimpleValueField,
  type TemplateField,
} from '../../types/templateModel'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../diffIndicators/ValueSetupDiffIndicators'
import { DocumentationEntry } from '../documentationEntry/DocumentationEntry'
import { TemplateDocumentationEntry } from '../documentationEntry/TemplateDocumentationEntry'
import { useFieldDocumentation } from '../templateFieldDocumentation/useFieldDocumentation'
import { TemplateFieldConfig } from './TemplateFieldConfig'

type Props = {
  field: TemplateField
  isCentered?: boolean
  isExpanded?: boolean
  errors?: Optional<
    ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  >
}

export const TemplateFieldTypeDetails = ({
  field,
  errors,
  isExpanded = false,
}: Props) => {
  const { modelFieldType } = useFieldDocumentation(field)
  const comparableField = useComparableField<NonConfigurationSimpleValueField>(
    field.path,
  )
  const translate = useTranslate()
  const configValueFromComparable = useCallback(
    ({
      value: comparable,
    }: SerialiseValueParams<NonConfigurationSimpleValueField>) =>
      comparable
        ? stringifyComparableValue(comparable.value?.valueConfig?.value)
        : '',
    [],
  )
  const {
    preferences: {
      templates: { isShowingEmptyValues },
    },
  } = useLayoutContext()
  const translateEnum = useTranslateEnum()

  return (
    <>
      <DocumentationEntry
        title={translate('templateViewer.fieldDetailsModal.field.type')}
        value={modelFieldType}
      />
      {field.fieldType !==
        ConfigurationFieldType.ConfigurationExpressionValue && (
        <TemplateDocumentationEntry
          title={translate('templateViewer.fieldDetailsModal.field.config')}
          diffIndicator={
            <ValueSetupDiffIndicators
              comparableValues={comparableField}
              serialiseValueFn={configValueFromComparable}
              compareFilter="valueConfig"
            />
          }
          value={<TemplateFieldConfig field={field} isExpanded={isExpanded} />}
          origin={field.value?.valueConfig?.origin}
          path={`${field.value?.path}.valueConfig`}
          errors={errors}
          isShowingErrors
        />
      )}
      {!field.value && isSubTemplatedField(field) && isShowingEmptyValues && (
        <DocumentationEntry
          title={translate('templateViewer.fieldDetailsModal.field.role')}
          value={<div>{translateEnum('enum.ValueSetupRole', field.role)}</div>}
        />
      )}
    </>
  )
}
